import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import bwipjs from 'bwip-js';
import { COLOR_BACKGROUND_KEY, COLOR_FOREGROUND_KEY } from '../../constants/storageKeys';
import ColorField from '../common/ColorField';
import { useLocalStorageState } from '../../utils/storage';
import { normalizeHex } from '../../utils/color';

function BarcodeToolPage({ tool }) {
  const canvasRef = useRef(null);
  const [value, setValue] = useState(tool.defaultValue || '');
  const [barColor, setBarColor] = useLocalStorageState(COLOR_FOREGROUND_KEY, '#000000');
  const [bgColor, setBgColor] = useLocalStorageState(COLOR_BACKGROUND_KEY, '#ffffff');
  const [transparent, setTransparent] = useState(false);
  const [error, setError] = useState('');

  const supportsSvg = typeof bwipjs.toSVG === 'function';

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (!value) {
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
      return;
    }
    try {
      const bar = normalizeHex(barColor, '#000000').replace('#', '');
      const background = transparent ? 'FFFFFF00' : normalizeHex(bgColor, '#ffffff').replace('#', '');
      bwipjs.toCanvas(canvas, {
        bcid: tool.bcid,
        text: value,
        scale: 3,
        height: 10,
        includetext: true,
        textxalign: 'center',
        barcolor: bar,
        backgroundcolor: background,
      });
      setError('');
    } catch (err) {
      setError(err?.message || 'Unable to render barcode.');
    }
  }, [value, barColor, bgColor, transparent, tool.bcid]);

  const downloadPng = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `${tool.id}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const downloadSvg = () => {
    if (!supportsSvg) return;
    try {
      const bar = normalizeHex(barColor, '#000000').replace('#', '');
      const background = transparent ? 'FFFFFF00' : normalizeHex(bgColor, '#ffffff').replace('#', '');
      const svg = bwipjs.toSVG({
        bcid: tool.bcid,
        text: value || ' ',
        scale: 3,
        height: 10,
        includetext: true,
        textxalign: 'center',
        barcolor: bar,
        backgroundcolor: background,
      });
      const blob = new Blob([svg], { type: 'image/svg+xml' });
      const link = document.createElement('a');
      link.download = `${tool.id}.svg`;
      link.href = URL.createObjectURL(blob);
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 0);
    } catch (err) {
      setError(err?.message || 'Unable to export SVG.');
    }
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
      <Stack spacing={3}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'center' }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {tool.label}
            </Typography>
            <Typography color="text.secondary">Barcode Generator</Typography>
            {tool.description && (
              <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                {tool.description}
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={1.5}>
            <Button variant="contained" onClick={downloadPng}>Download PNG</Button>
            {supportsSvg && (
              <Button variant="outlined" onClick={downloadSvg}>Download SVG</Button>
            )}
          </Stack>
        </Stack>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 7 }}>
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Value"
                placeholder="Enter barcode value"
                value={value}
                onChange={(event) => setValue(event.target.value)}
                helperText={tool.guidance}
              />
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <ColorField
                    label="Foreground Color (hex)"
                    placeholder="#000000"
                    value={barColor}
                    onChange={setBarColor}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <ColorField
                    label="Background Color (hex)"
                    placeholder="#ffffff"
                    value={bgColor}
                    onChange={setBgColor}
                    disabled={transparent}
                  />
                </Grid>
              </Grid>
              <FormControlLabel
                control={
                  <Switch
                    checked={transparent}
                    onChange={(event) => setTransparent(event.target.checked)}
                  />
                }
                label="Transparent background"
              />
              {error && <Alert severity="error">{error}</Alert>}
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 5 }}>
            <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>Preview</Typography>
              <Box component="canvas" ref={canvasRef} sx={{ width: '100%', height: 'auto' }} />
            </Paper>
          </Grid>
        </Grid>
      </Stack>
    </Paper>
  );
}

export default BarcodeToolPage;
